<script setup lang="ts">
import {format} from 'date-fns-tz'
import {nl} from 'date-fns/locale';
import {FormControl, FormDescription, FormItem, FormLabel, FormMessage} from "@/shared/components/ui/form2/";
import {computed, HTMLAttributes, ref} from 'vue'
import {Button} from "@/shared/components/ui/button";
import {Calendar} from '@/shared/components/ui/calendar2'
import {FormItemProps} from ".";
import {gridItemVariants} from "@/shared/components/ui/helper/";
import {cn} from "@/shared/lib/utils.ts";
import {Popover, PopoverContent, PopoverTrigger,} from '@/shared/components/ui/popover'
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";
import {faCalendarAlt} from "@fortawesome/pro-solid-svg-icons";
import FormError from "@/shared/components/ui/form2/FormError.vue";
import {DateValue, getLocalTimeZone} from '@internationalized/date'
import {toDateValue} from "@/shared/composable/utils.ts";
import {useI18n} from "vue-i18n";

const {t} = useI18n()

interface FormDatePickerProps extends FormItemProps {
  mode?: 'date' | 'datetime',
  numberOfMonths?: number,
  format?: string,
  inline?: boolean,
  class?: HTMLAttributes['class'],
  disabledDays?: any,
  minDate?: Date;
  maxDate?: Date;
  isDateDisabled?: (date: Date) => boolean;
  isDateUnavailable?: (date: Date) => boolean;
}

const props = withDefaults(defineProps<FormDatePickerProps>(), {
  mode: 'date',
  numberOfMonths: 1,
  inline: false
});

// Use defineModel instead of props/emit
const modelValue = defineModel<string | Date>()

// Create a computed for the calendar that handles the conversion
const calendarModel = computed({
  get: () => {
    if (typeof modelValue.value === 'string') {
      return toDateValue(new Date(modelValue.value))
    } else if (modelValue.value instanceof Date) {
      return toDateValue(modelValue.value)
    }
    return null
  },
  set: (value: DateValue) => {
    if (!value) {
      modelValue.value = null
    } else {
      openState.value = false
      modelValue.value = value.toDate(getLocalTimeZone())
    }
  }
})

const formattedValue = computed(() => {
  if (!modelValue.value) return t('shared.date_picker.choose_date')
  let dateValue: Date | null = null;

  if (typeof modelValue.value === 'string') {
    dateValue = new Date(modelValue.value)
  } else if (modelValue.value instanceof Date) {
    dateValue = modelValue.value
  }
  if (props.mode == 'date') return format(dateValue, dateFormat.value, {locale: nl})
  if (props.mode == 'datetime') return format(dateValue, dateFormat.value, {locale: nl})
})

const openState = ref<boolean>(false)
const dateFormat = computed(() => {
  if (props.format) {
    return props.format;
  }
  return props.mode === 'datetime' ? "PPP - HH:mm" : "PPP";
});
const hasError = computed(() => props.error != undefined)

const isDateDisabled = (dateValue: DateValue) => {
  if (!props.isDateDisabled) return false

  return props.isDateDisabled(dateValue.toDate(getLocalTimeZone()))
}
const isDateUnavailable = (dateValue: DateValue) => {
  if (!props.isDateUnavailable) return false
  return props.isDateUnavailable(dateValue.toDate(getLocalTimeZone()))
}

</script>

<template>
  <FormItem :class="cn(gridItemVariants({ colSpan }), 'form-date-picker', props.class)">
    <FormLabel v-if="label" :error="hasError">{{ label }}{{ required ? '*' : '' }}</FormLabel>
    <template v-if="inline">
      <FormControl>
        <Calendar
            v-model="calendarModel"
            :mode="mode"
            :is24hr="true"
            :numberOfMonths="numberOfMonths"
            :weekStartsOn="1"
            locale="nl"
            class="rounded-md border"
        />
      </FormControl>
    </template>
    <template v-else>
      <Popover>
        <PopoverTrigger as-child>
          <FormControl class="flex">
            <Button
                variant="hollow"
                :class="cn(
                'lex justify-start h-10 w-full rounded-md border border-slate-200 bg-white pr-3 py-2 text-sm ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-800 dark:bg-slate-950 dark:ring-offset-slate-950 dark:placeholder:text-slate-400 dark:focus-visible:ring-slate-300',
                'ps-3 text-start font-normal',
                !modelValue && 'text-muted-foreground',
                hasError ? 'border-red-600':'',
                props.elementClass
              )"
            >
              <FontAwesomeIcon :icon="faCalendarAlt" class="pr-2 h-4 w-4 opacity-50"/>
              <span>{{ formattedValue }}</span>
            </Button>
          </FormControl>
        </PopoverTrigger>
        <PopoverContent class="p-0 w-full">
          <Calendar
              v-model="calendarModel"
              :weekday-format="'short'"
              :numberOfMonths="numberOfMonths"
              :weekStartsOn="1"
              :max-value="toDateValue(maxDate)"
              :min-value="toDateValue(minDate)"
              :is-date-disabled="isDateDisabled"
              :is-date-unavailable="isDateUnavailable"
              class="rounded-md border"
          />
        </PopoverContent>
      </Popover>
      <FormError :error="error"/>
    </template>
    <FormDescription v-if="description" v-html="description"/>
    <FormMessage/>
  </FormItem>
</template>